// Utility functions to automatically fix common diagram syntax issues

export function autoFixDiagramSyntax(code) {
  if (!code || !code.trim()) {
    return { fixed: code, changes: [] };
  }

  let fixedCode = code;
  const changes = [];

  // Fix 1: Remove invalid Mermaid syntax like << >> comments
  const invalidCommentPattern = /<<[^>]*>>/g;
  const invalidComments = fixedCode.match(invalidCommentPattern);
  if (invalidComments) {
    invalidComments.forEach(comment => {
      const commentContent = comment.replace(/[<>]/g, '').trim();
      // Convert to proper Mermaid comment
      fixedCode = fixedCode.replace(comment, `%% ${commentContent}`);
      changes.push(`Fixed invalid comment: ${comment} → %% ${commentContent}`);
    });
  }

  // Fix 2: Quote all unquoted labels with special characters (preserve original text)
  const complexNodePattern = /(\w+)\[([^\]"']+)\]/g;
  fixedCode = fixedCode.replace(complexNodePattern, (match, nodeId, label) => {
    // Skip if already quoted
    if (label.startsWith('"') || label.startsWith("'")) {
      return match;
    }
    
    // Check if label needs quoting (has special chars, spaces, or complex punctuation)
    if (needsQuoting(label)) {
      changes.push(`Added quotes to preserve special characters: ${nodeId}[${label}]`);
      return `${nodeId}["${label}"]`;
    }
    
    return match;
  });

  // Fix 3: Clean up extra whitespace and ensure proper formatting
  fixedCode = cleanupFormatting(fixedCode);

  return {
    fixed: fixedCode,
    changes,
    hasChanges: changes.length > 0
  };
}

// Enhanced function to determine if a label needs quoting
function needsQuoting(label) {
  // Always quote if contains these characters
  const alwaysQuoteChars = /[&()!@#$%^*+={}|\\:";'<>?,./]/;
  
  // Quote if has multiple words (spaces)
  const hasSpaces = /\s/.test(label);
  
  // Quote if has complex punctuation or mixed content
  const hasComplexPunctuation = /[(),&/\\-]/.test(label);
  
  // Quote if looks like a technical description (has parentheses with content)
  const hasTechnicalDescription = /\([^)]+\)/.test(label);
  
  return alwaysQuoteChars.test(label) || 
         hasSpaces || 
         hasComplexPunctuation || 
         hasTechnicalDescription;
}

// Clean up formatting issues
function cleanupFormatting(code) {
  return code
    // Remove multiple consecutive empty lines
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    // Ensure proper line endings
    .replace(/\r\n/g, '\n')
    // Trim trailing whitespace from lines
    .replace(/[ \t]+$/gm, '')
    // Ensure file ends with single newline
    .replace(/\n*$/, '\n');
}

export function generateSuggestedFixes(error, code) {
  const suggestions = [];

  if (!error || !code) {
    return suggestions;
  }

  const errorStr = error.toString().toLowerCase();

  // Enhanced fix suggestions based on error patterns
  if (errorStr.includes('ps') || errorStr.includes('expecting')) {
    suggestions.push({
      title: "Quote Complex Labels",
      description: "Wrap node labels containing special characters, spaces, or technical descriptions in quotes",
      example: `API["API Gateway / Edge Services"]`,
      action: "auto-quote"
    });
  }

  if (code.includes('<<') && code.includes('>>')) {
    suggestions.push({
      title: "Fix Invalid Syntax",
      description: "Convert << >> syntax to proper Mermaid comments using %%",
      example: `<< Comment >> → %% Comment`,
      action: "fix-comments"
    });
  }

  if (code.includes('&') || code.includes('()') || code.includes('/')) {
    suggestions.push({
      title: "Quote Technical Descriptions",
      description: "Technical descriptions with special characters need quotes",
      example: `Micro["Microservices Layer (EKS, ECS, Lambda)"]`,
      action: "quote-technical"
    });
  }

  if (errorStr.includes('flowchart') || errorStr.includes('graph')) {
    suggestions.push({
      title: "Check Node Definitions",
      description: "Ensure all nodes have proper syntax with quoted complex labels",
      example: `A["Service Name"] --> B["Another Service"]`,
      action: "validate-flowchart"
    });
  }

  // Add general suggestions
  suggestions.push({
    title: "Use Auto-Fix",
    description: "Try the automatic fix button to resolve multiple syntax issues at once",
    example: `Click "🔧 Auto Fix" above`,
    action: "auto-fix"
  });

  return suggestions.slice(0, 4); // Limit to 4 most relevant suggestions
}

export function getWorkingExamples() {
  return {
    aws: {
      title: "AWS Architecture",
      code: `graph TD
    A[User] --> B["Amazon Cognito"]
    B --> C{Authenticated?}
    C -->|Yes| D["JWT Token"]
    C -->|No| E["Login Page"]
    D --> F["API Gateway"]
    F --> G["Lambda Function"]
    G --> H["DynamoDB"]
    E --> A`
    },
    enterprise: {
      title: "Enterprise Architecture",
      code: `flowchart TD
    Client["Client Portal Apps"]
    API["API Gateway / Edge Services"]
    Auth["Identity & Access Mgmt (Amazon Cognito)"]
    Micro["Microservices Layer (EKS, ECS, Lambda)"]
    AML["AML Transaction Analysis (ML & AI)"]
    KYC["KYC Onboarding & Verification (Step Functions, Lex, Rekognition)"]
    DB["Multi-model DBs (Aurora, DynamoDB, S3 Data Lake)"]
    Events["Event-Driven Processing (EventBridge, SNS/SQS)"]
    Sec["Security & Compliance (Config, Macie, SecurityHub)"]
    Audit["Audit Trail & Reporting"]
    Analytics["Real-Time Analytics (Kinesis, QuickSight)"]
    
    %% Cloud-Native Environment
    
    Client --> API
    API --> Auth
    Auth --> Micro
    Micro --> AML
    Micro --> KYC
    AML --> Events
    KYC --> Events
    Events --> DB
    Events --> Analytics
    Analytics --> Audit
    Sec -.-> Micro
    Sec -.-> DB
    Sec -.-> Audit
    DB --> Analytics`
    },
    flowchart: {
      title: "Basic Flowchart",
      code: `graph TD
    A[Start] --> B{"Is it working?"}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B
    C --> E[End]`
    },
    process: {
      title: "Process Flow",
      code: `graph LR
    A["Input Data"] --> B["Validate"]
    B --> C{"Valid?"}
    C -->|Yes| D["Process"]
    C -->|No| E["Error"]
    D --> F["Output"]
    E --> A`
    }
  };
}